import { Injectable } from "@angular/core";
import {
  FilterGroup,
  FilterInterface,
  FilterSet,
} from "src/app/core/interfaces/filter-interface";
import { BackendService } from "src/app/core/services/backend-service";

@Injectable({
  providedIn: "root",
})
export class FiltersService extends BackendService {
  // HTTP requests to Backend API
  getFilterGroup(): Promise<FilterGroup | null> {
    return this.get<FilterGroup | null>("api/filters");
  }

  getJourneyFilterGroup(): Promise<FilterGroup | null> {
    return this.get<FilterGroup | null>("api/filters/journey-filter-group");
  }

  getGeographicalLocationFilterSet(): Promise<FilterSet | null> {
    return this.get<FilterSet | null>("api/filters/geographical-location");
  }

  getActivitiesFilterSetForVinciJob(): Promise<FilterSet | null> {
    return this.get<FilterSet | null>("api/filters/vinci-job-activities");
  }

  // Shared Logic between differents parts of the project
  getAllFilterCategories(parentFilters: FilterInterface[]) {
    let filterCategories: FilterInterface[] = [];
    const retrievedFilterCategories = parentFilters.filter(
      (f) => f.filters !== null
    );
    if (retrievedFilterCategories.length > 0) {
      filterCategories = filterCategories.concat(retrievedFilterCategories);
      retrievedFilterCategories.forEach((fc) => {
        filterCategories = filterCategories.concat(
          this.getAllFilterCategories(fc.filters)
        );
      });
    }
    return filterCategories;
  }

  // Shared Logic between differents parts of the project
  getTopLevelsGeographicalLocationFilterCategories(
    parentFilters: FilterInterface[],
    depth: number = 0
  ) {
    let filterCategories: FilterInterface[] = [];
    if (depth < 1) {
      const retrievedFilterCategories = parentFilters.filter(
        (f) => f.filters !== null
      );
      if (retrievedFilterCategories.length > 0) {
        filterCategories = filterCategories.concat(retrievedFilterCategories);
        retrievedFilterCategories.forEach((fc) => {
          filterCategories = filterCategories.concat(
            this.getTopLevelsGeographicalLocationFilterCategories(
              fc.filters,
              depth + 1
            )
          );
        });
      }
    }

    return filterCategories;
  }

  updateFilterTranslations(
    currentFilter: FilterInterface,
    updatedFilter: FilterInterface
  ): FilterInterface {
    currentFilter.translation = updatedFilter.translation;
    currentFilter.filters.forEach((childFilter) => {
      const updatedChildFilter = updatedFilter.filters.find(
        (ucf) => ucf.spId === childFilter.spId
      );
      childFilter = this.updateFilterTranslations(
        childFilter,
        updatedChildFilter
      );
    });

    return currentFilter;
  }

  findInFilterHierarchy(
    parentFilter: FilterInterface,
    spId: string,
    maxDepth = 1
  ): FilterInterface | null {
    if (maxDepth == 10) {
      return null;
    }

    if (parentFilter.filters) {
      for (const f of parentFilter.filters) {
        if (f.spId == spId) {
          return f;
        }

        const foundMatch = this.findInFilterHierarchy(f, spId, maxDepth + 1);
        if (foundMatch) {
          return foundMatch;
        }
      }
    }

    return null;
  }

  findInHierarchy(filterSet: FilterSet, spId: string): FilterInterface | null {
    for (const fc of filterSet.filtersCategories) {
      if (fc.spId == spId) {
        return fc;
      }
      const foundMatch = this.findInFilterHierarchy(fc, spId);
      if (foundMatch) {
        return foundMatch;
      }
    }

    for (const f of filterSet.filters) {
      if (f.spId == spId) {
        return f;
      }
      const foundMatch = this.findInFilterHierarchy(f, spId);
      if (foundMatch) {
        return foundMatch;
      }
    }

    return null;
  }

  updateFilterSetTranslations(
    currentFilterSet: FilterSet,
    updatedFilterSet: FilterSet
  ): FilterSet {
    currentFilterSet.localName = updatedFilterSet.localName;
    currentFilterSet.filters.forEach((f) => {
      const updatedFilter = this.findInHierarchy(updatedFilterSet, f.spId);
      if (updatedFilter) {
        this.updateFilterTranslationsRecursive(f, updatedFilter);
      }
    });

    if (currentFilterSet.filtersCategories !== null) {
      currentFilterSet.filtersCategories.forEach((fc) => {
        const updatedFilterCategory = this.findInHierarchy(
          updatedFilterSet,
          fc.spId
        );
        if (updatedFilterCategory) {
          this.updateFilterTranslationsRecursive(fc, updatedFilterCategory);
        }
      });
    }

    return currentFilterSet;
  }

  private updateFilterTranslationsRecursive(
    currentFilter: FilterInterface,
    updatedFilter: FilterInterface
  ): void {
    currentFilter.translation = updatedFilter.translation;

    if (currentFilter.filters && currentFilter.filters.length > 0) {
      currentFilter.filters.forEach((childFilter) => {
        const updatedChildFilter = this.findInFilterHierarchy(
          updatedFilter,
          childFilter.spId
        );
        if (updatedChildFilter) {
          this.updateFilterTranslationsRecursive(
            childFilter,
            updatedChildFilter
          );
        }
      });
    }
  }
}
